import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import {
  GetHumanTasksResponse,
  AcceptHumanTaskResponse,
  PickNextTaskResponse,
  HumanTask,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import { useCompany } from '../../contexts/CompanyContext';
import './human-tasks.scss';

interface Intervention {
  commandId: string;
  sessionId: string;
  instanceId: string;
  threadId: string;
  userId: string;
  message: string;
  vncUrl: string;
  bitwardenCredentialId: string | null;
  status: 'pending' | 'active' | 'resolved';
  createdAt: number;
}

interface RevealedCredential {
  credentialId: string;
  label: string;
  site: string | null;
  credentialType: string;
  fields: Record<string, string> | null;
  error?: string;
}

// Simple inline SVG icons
const PhoneIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' />
  </svg>
);

const GlobeIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <line x1='2' y1='12' x2='22' y2='12' />
    <path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
  </svg>
);

const MapPinIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
    <circle cx='12' cy='10' r='3' />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='9 18 15 12 9 6' />
  </svg>
);

const MonitorIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='2' y='3' width='20' height='14' rx='2' ry='2' />
    <line x1='8' y1='21' x2='16' y2='21' />
    <line x1='12' y1='17' x2='12' y2='21' />
  </svg>
);

const LifeBuoyIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <circle cx='12' cy='12' r='4' />
    <line x1='4.93' y1='4.93' x2='9.17' y2='9.17' />
    <line x1='14.83' y1='14.83' x2='19.07' y2='19.07' />
    <line x1='14.83' y1='9.17' x2='19.07' y2='4.93' />
    <line x1='14.83' y1='9.17' x2='18.36' y2='5.64' />
    <line x1='4.93' y1='19.07' x2='9.17' y2='14.83' />
  </svg>
);

const HumanTasks: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { opsRole } = useCompany();
  const isAdmin = opsRole === 'ops_admin';
  const [activeTasks, setActiveTasks] = useState<HumanTask[]>([]);
  const [availableTasks, setAvailableTasks] = useState<HumanTask[]>([]);
  const [issueTasks, setIssueTasks] = useState<HumanTask[]>([]);
  const [completedTasks, setCompletedTasks] = useState<HumanTask[]>([]);
  const [showCompleted, setShowCompleted] = useState<boolean>(false);
  const [completedLoading, setCompletedLoading] = useState<boolean>(false);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [acceptingTaskId, setAcceptingTaskId] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [pickingNext, setPickingNext] = useState<boolean>(false);
  const [revealedCreds, setRevealedCreds] = useState<
    Record<string, RevealedCredential[]>
  >({});
  const [credLoading, setCredLoading] = useState<string | null>(null);
  const [credError, setCredError] = useState<Record<string, string>>({});
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());

  const pusherContext = usePusher(true) as any;

  useEffect(() => {
    loadTasks();
    loadInterventions();
  }, []);

  // Pusher real-time updates for interventions
  useEffect(() => {
    const pusherInstance = pusherContext?.pusherInstance;
    if (!pusherInstance) return;

    const channel = pusherInstance.subscribe('browser-interventions');
    channel.bind('new-intervention', () => loadInterventions());
    channel.bind('intervention-resolved', () => loadInterventions());

    return () => {
      channel.unbind_all();
      pusherInstance.unsubscribe('browser-interventions');
    };
  }, [pusherContext?.pusherInstance]);

  const loadInterventions = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get('/browser-agent/interventions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInterventions(response.data.interventions || []);
    } catch (err) {
      // Silently fail — interventions are supplementary
      console.error('Error loading interventions:', err);
    }
  };

  const handleRevealCredentials = useCallback(
    async (commandId: string) => {
      try {
        setCredLoading(commandId);
        setCredError((prev) => {
          const next = { ...prev };
          delete next[commandId];
          return next;
        });
        const token = await getAccessTokenSilently();
        const response = await api.get(
          `/browser-agent/interventions/${commandId}/credentials/reveal`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.data.success) {
          setRevealedCreds((prev) => ({
            ...prev,
            [commandId]: response.data.credentials ?? [],
          }));
        } else {
          setCredError((prev) => ({
            ...prev,
            [commandId]: response.data.error || 'Failed to reveal credentials',
          }));
        }
      } catch (err: any) {
        const msg = err.response?.data?.error || 'Failed to reveal credentials';
        setCredError((prev) => ({ ...prev, [commandId]: msg }));
      } finally {
        setCredLoading(null);
      }
    },
    [getAccessTokenSilently],
  );

  const toggleFieldVisibility = useCallback((fieldKey: string) => {
    setVisibleFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }
      return next;
    });
  }, []);

  const maskValue = (value: string) => '•'.repeat(Math.min(value.length, 20));

  const handleResolveIntervention = async (commandId: string) => {
    try {
      setResolvingId(commandId);
      const token = await getAccessTokenSilently();
      await api.post(
        `/browser-agent/interventions/${commandId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      loadInterventions();
    } catch (err) {
      console.error('Error resolving intervention:', err);
    } finally {
      setResolvingId(null);
    }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();

      // Fetch all tasks
      const response = await api.get<GetHumanTasksResponse>('/human-tasks', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.tasks) {
        // Separate into active (accepted by current user) and available (pending).
        // Issue reports (help_request) are split into their own bucket so they
        // show in a dedicated "Reported Issues" section for ops admins and don't
        // mix in with normal delegation work.
        const active = response.data.tasks.filter(
          (task) =>
            (task.status === 'accepted' || task.status === 'in_progress') &&
            task.taskType !== 'help_request',
        );
        const available = response.data.tasks.filter(
          (task) =>
            task.status === 'pending' && task.taskType !== 'help_request',
        );
        const issues = response.data.tasks.filter(
          (task) =>
            task.taskType === 'help_request' &&
            (task.status === 'pending' ||
              task.status === 'accepted' ||
              task.status === 'in_progress'),
        );

        // Sort by deadline (soonest first), tasks without deadline go last
        const sortByDeadline = (a: HumanTask, b: HumanTask) => {
          if (!a.deadline && !b.deadline) return 0;
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return (
            new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
          );
        };

        setActiveTasks(active.sort(sortByDeadline));
        setAvailableTasks(available.sort(sortByDeadline));
        setIssueTasks(issues.sort(sortByDeadline));
      } else {
        setError('Failed to load tasks');
      }
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      // Check if it's a 403 forbidden error (no access)
      if (err.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setError(err.response?.data?.error || 'Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTask = async (taskId: string) => {
    try {
      setAcceptingTaskId(taskId);
      const token = await getAccessTokenSilently();

      const response = await api.post<AcceptHumanTaskResponse>(
        `/human-tasks/${taskId}/accept`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (
        response.data.success &&
        response.data.threadId &&
        response.data.task
      ) {
        // Navigate to the thread - use 'other' as agent_id since ops users
        // don't have the task creator's agent in their agent list
        navigate(
          `/conversations?agent_id=other&thread_id=${response.data.threadId}`,
        );
      } else {
        setError('Failed to accept task');
      }
    } catch (err: any) {
      console.error('Error accepting task:', err);
      setError(err.response?.data?.error || 'Failed to accept task');
    } finally {
      setAcceptingTaskId(null);
    }
  };

  const handleOpenThread = (threadId: string, readOnly?: boolean) => {
    // Use 'other' as agent_id since ops users access agents from other companies
    const params = `agent_id=other&thread_id=${threadId}`;
    navigate(`/conversations?${params}${readOnly ? '&read_only=true' : ''}`);
  };

  // Pending interventions sorted FIFO (oldest first)
  const pendingInterventions = interventions
    .filter((i) => i.status === 'pending')
    .sort((a, b) => a.createdAt - b.createdAt);

  const hasNextTask =
    pendingInterventions.length > 0 || availableTasks.length > 0;

  const handlePickNext = async () => {
    if (pickingNext) return;
    setPickingNext(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<PickNextTaskResponse>(
        '/human-tasks/pick-next',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const data = response.data;
      if (!data.success) {
        // No tasks available — just refresh the list
        await loadTasks();
        return;
      }

      if (data.type === 'intervention' && data.intervention?.vncUrl) {
        window.open(data.intervention.vncUrl, '_blank');
      } else if (data.type === 'task' && data.threadId) {
        navigate(`/conversations?agent_id=other&thread_id=${data.threadId}`);
      }

      // Refresh lists after picking
      await loadTasks();
    } catch (err: any) {
      console.error('Error picking next task:', err);
      if (err.response?.status === 409) {
        // Race condition — another user grabbed it, refresh and retry
        await loadTasks();
      } else {
        setError(err.response?.data?.error || 'Failed to pick next task');
      }
    } finally {
      setPickingNext(false);
    }
  };

  const loadCompletedTasks = async () => {
    try {
      setCompletedLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetHumanTasksResponse>('/human-tasks', {
        headers: { Authorization: `Bearer ${token}` },
        params: { includeCompleted: 'true' },
      });

      if (response.data.success && response.data.tasks) {
        const terminal = response.data.tasks.filter(
          (task) =>
            task.status === 'completed' ||
            task.status === 'cancelled' ||
            task.status === 'failed' ||
            task.status === 'handed_off' ||
            task.status === 'closed',
        );
        // Sort by most recently updated
        terminal.sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt).getTime() -
            new Date(a.updatedAt || a.createdAt).getTime(),
        );
        setCompletedTasks(terminal);
      }
    } catch (err) {
      console.error('Error loading completed tasks:', err);
    } finally {
      setCompletedLoading(false);
    }
  };

  useEffect(() => {
    if (showCompleted) {
      loadCompletedTasks();
    } else {
      setCompletedTasks([]);
    }
  }, [showCompleted]);

  const formatTaskType = (taskType: string) => {
    if (taskType === 'phone_call') return 'Phone Call';
    if (taskType === 'web_action') return 'Web Action';
    if (taskType === 'in_person') return 'In-Person';
    if (taskType === 'help_request') return 'Issue Report';
    return taskType;
  };

  const formatDeadline = (deadline: string | undefined) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    // Format based on urgency
    if (diffHours < 0) {
      return { text: 'Overdue', className: 'deadline-overdue' };
    } else if (diffHours < 2) {
      return {
        text: `Due in ${Math.ceil(diffHours * 60)} min`,
        className: 'deadline-urgent',
      };
    } else if (diffHours < 24) {
      return {
        text: `Due in ${Math.ceil(diffHours)}h`,
        className: 'deadline-soon',
      };
    } else {
      return {
        text: date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        }),
        className: 'deadline-normal',
      };
    }
  };

  if (loading) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='loading-state'>Loading tasks...</div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='access-denied-state'>
            <h3>Access Denied</h3>
            <p>This page is only accessible to internal ops and super users.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='error-state'>{error}</div>
        </div>
      </div>
    );
  }

  const TaskTypeIcon = ({ taskType }: { taskType: string }) => {
    if (taskType === 'phone_call') return <PhoneIcon />;
    if (taskType === 'web_action') return <GlobeIcon />;
    if (taskType === 'in_person') return <MapPinIcon />;
    if (taskType === 'help_request') return <LifeBuoyIcon />;
    return null;
  };

  // High-impact priority badge — flagged by humanTaskEstimateSpecialist
  // (large purchases, flight bookings, flight check-ins). Backend filters
  // priority=1 tasks out of the ops_worker view, so this badge only ever
  // renders for ops admins.
  const PriorityBadge = ({ priority }: { priority?: number }) =>
    priority === 1 ? (
      <span
        className='task-priority-badge task-priority-p1'
        title='High impact — admin only'
      >
        P1
      </span>
    ) : null;

  // Build the pick-next button label
  const nextIsIntervention = pendingInterventions.length > 0;
  const pendingCount = pendingInterventions.length + availableTasks.length;
  const pickNextLabel = pickingNext
    ? 'Picking...'
    : !hasNextTask
      ? 'No tasks available'
      : nextIsIntervention
        ? `Browser Intervention (${pendingCount} pending)`
        : `Pick Next Task (${pendingCount} pending)`;

  // Shared pick-next button
  const PickNextButton = () => (
    <button
      className={`pick-next-btn ${!hasNextTask ? 'pick-next-btn--empty' : ''} ${nextIsIntervention ? 'pick-next-btn--intervention' : ''}`}
      onClick={handlePickNext}
      disabled={!hasNextTask || pickingNext}
    >
      {pickingNext ? (
        <span className='loading-spinner' />
      ) : (
        <ChevronRightIcon />
      )}
      <span>{pickNextLabel}</span>
    </button>
  );

  // Shared active tasks list
  const ActiveTasksList = () => (
    <div className='tasks-section active-section'>
      <h4 className='section-title'>My Active Tasks</h4>
      {activeTasks.length === 0 ? (
        <div className='empty-state'>No active tasks</div>
      ) : (
        <div className='tasks-list'>
          {activeTasks.map((task) => {
            const deadlineInfo = formatDeadline(task.deadline);
            return (
              <div
                key={task.id}
                className='task-row active-task'
                onClick={() => task.threadId && handleOpenThread(task.threadId)}
              >
                <div className='task-type-icon'>
                  <TaskTypeIcon taskType={task.taskType} />
                </div>
                <div className='task-info'>
                  <span className='task-title'>
                    <PriorityBadge priority={task.priority} />
                    {task.title}
                  </span>
                  <span className='task-meta'>
                    {task.acceptedByUserName && (
                      <span className='task-worker'>
                        {task.acceptedByUserName}
                      </span>
                    )}
                    {deadlineInfo ? (
                      <span
                        className={`task-deadline ${deadlineInfo.className}`}
                      >
                        {deadlineInfo.text}
                      </span>
                    ) : (
                      <span className='task-status-badge'>{task.status}</span>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Shared completed tasks section
  const CompletedTasksSection = () =>
    showCompleted ? (
      <div className='tasks-section completed-section'>
        <h4 className='section-title'>
          Completed / Handed Off / Failed / Cancelled
        </h4>
        {completedLoading ? (
          <div className='empty-state'>Loading...</div>
        ) : completedTasks.length === 0 ? (
          <div className='empty-state'>No completed tasks</div>
        ) : (
          <div className='tasks-list'>
            {completedTasks.map((task) => (
              <div
                key={task.id}
                className={`task-row completed-task status-${task.status}`}
                onClick={() =>
                  task.threadId && handleOpenThread(task.threadId, true)
                }
              >
                <div className='task-type-icon'>
                  <TaskTypeIcon taskType={task.taskType} />
                </div>
                <div className='task-info'>
                  <span className='task-title'>{task.title}</span>
                  <span className='task-meta'>
                    {task.acceptedByUserName && (
                      <span className='task-worker'>
                        {task.acceptedByUserName}
                      </span>
                    )}
                    <span className={`task-status-badge status-${task.status}`}>
                      {task.status}
                    </span>
                  </span>
                </div>
                <span className='task-date'>
                  {new Date(
                    task.updatedAt || task.createdAt,
                  ).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    ) : null;

  // ─── Ops Worker View: simple pick-next + active tasks ───────────
  if (!isAdmin) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='page-header-area'>
            <div className='title-and-text'>
              <h3>Tasks</h3>
            </div>
            <button
              className={`completed-toggle-btn ${showCompleted ? 'active' : ''}`}
              onClick={() => setShowCompleted((prev) => !prev)}
            >
              {showCompleted ? 'Hide Completed' : 'Show Completed'}
            </button>
          </div>

          <div className='worker-view'>
            <PickNextButton />
            <ActiveTasksList />
            <CompletedTasksSection />
          </div>
        </div>
      </div>
    );
  }

  // ─── Ops Admin View: pick-next + full visibility ────────────────
  return (
    <div className='page-wrapper'>
      <div className='page-inner-wrapper human-tasks-page'>
        <div className='page-header-area'>
          <div className='title-and-text'>
            <h3>Human Tasks</h3>
          </div>
          <div className='header-actions'>
            <button
              className={`completed-toggle-btn ${showCompleted ? 'active' : ''}`}
              onClick={() => setShowCompleted((prev) => !prev)}
            >
              {showCompleted ? 'Hide Completed' : 'Show Completed'}
            </button>
          </div>
        </div>

        <PickNextButton />

        <div className='tasks-container'>
          {/* Reported Issues Section — admin-only, distinct from delegation tasks */}
          {issueTasks.length > 0 && (
            <div className='tasks-section issues-section'>
              <h4 className='section-title'>
                Reported Issues ({issueTasks.length})
              </h4>
              <div className='tasks-list'>
                {issueTasks.map((task) => {
                  const deadlineInfo = formatDeadline(task.deadline);
                  return (
                    <div
                      key={task.id}
                      className={`task-row issue-task status-${task.status}`}
                      onClick={() =>
                        task.threadId
                          ? handleOpenThread(task.threadId)
                          : handleAcceptTask(task.id)
                      }
                    >
                      <div className='task-type-icon'>
                        <TaskTypeIcon taskType={task.taskType} />
                      </div>
                      <div className='task-info'>
                        <span className='task-title'>
                          <PriorityBadge priority={task.priority} />
                          {task.title}
                        </span>
                        <span className='task-meta'>
                          <span className='task-status-badge'>
                            {task.status === 'pending' ? 'New' : task.status}
                          </span>
                          {task.acceptedByUserName && (
                            <span className='task-worker'>
                              {task.acceptedByUserName}
                            </span>
                          )}
                          {deadlineInfo && (
                            <span
                              className={`task-deadline ${deadlineInfo.className}`}
                            >
                              {deadlineInfo.text}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available Tasks Section */}
          <div className='tasks-section available-section'>
            <h4 className='section-title'>Available</h4>
            {availableTasks.length === 0 ? (
              <div className='empty-state'>No tasks available</div>
            ) : (
              <div className='tasks-list'>
                {availableTasks.map((task) => {
                  const deadlineInfo = formatDeadline(task.deadline);
                  return (
                    <div key={task.id} className='task-row available-task'>
                      <div className='task-type-icon'>
                        <TaskTypeIcon taskType={task.taskType} />
                      </div>
                      <div className='task-info'>
                        <span className='task-title'>
                          <PriorityBadge priority={task.priority} />
                          {task.title}
                        </span>
                        {deadlineInfo ? (
                          <span
                            className={`task-deadline ${deadlineInfo.className}`}
                          >
                            {deadlineInfo.text}
                          </span>
                        ) : (
                          <span className='task-date'>
                            {new Date(task.createdAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <button
                        className='accept-btn'
                        onClick={() => handleAcceptTask(task.id)}
                        disabled={acceptingTaskId === task.id}
                        title='Accept Task'
                      >
                        {acceptingTaskId === task.id ? (
                          <span className='loading-spinner' />
                        ) : (
                          <ChevronRightIcon />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Browser Interventions Section */}
          {interventions.length > 0 && (
            <div className='tasks-section interventions-section'>
              <h4 className='section-title'>Browser Interventions</h4>
              <div className='tasks-list'>
                {interventions.map((intervention) => {
                  const ageMs = Date.now() - intervention.createdAt;
                  const ageMins = Math.floor(ageMs / 60000);
                  const ageText =
                    ageMins < 1
                      ? 'Just now'
                      : ageMins < 60
                        ? `${ageMins}m ago`
                        : `${Math.floor(ageMins / 60)}h ago`;

                  return (
                    <div
                      key={intervention.commandId}
                      className='task-row intervention-task'
                    >
                      <div className='task-type-icon'>
                        <MonitorIcon />
                      </div>
                      <div className='task-info'>
                        <span className='task-title'>
                          {intervention.message}
                        </span>
                        <span className='task-date'>{ageText}</span>
                      </div>
                      <div className='intervention-actions'>
                        {intervention.bitwardenCredentialId && (
                          <button
                            className='connect-btn'
                            onClick={() =>
                              handleRevealCredentials(intervention.commandId)
                            }
                            disabled={credLoading === intervention.commandId}
                          >
                            {credLoading === intervention.commandId
                              ? 'Loading...'
                              : revealedCreds[intervention.commandId]
                                ? 'Refresh'
                                : 'Credentials'}
                          </button>
                        )}
                        {intervention.vncUrl && (
                          <a
                            href={intervention.vncUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='connect-btn'
                          >
                            Connect
                          </a>
                        )}
                        <button
                          className='resolve-btn'
                          onClick={() =>
                            handleResolveIntervention(intervention.commandId)
                          }
                          disabled={resolvingId === intervention.commandId}
                        >
                          {resolvingId === intervention.commandId
                            ? 'Resolving...'
                            : 'Resolve'}
                        </button>
                      </div>
                      {credError[intervention.commandId] && (
                        <div className='credential-error'>
                          {credError[intervention.commandId]}
                        </div>
                      )}
                      {revealedCreds[intervention.commandId] &&
                        revealedCreds[intervention.commandId].length > 0 && (
                          <div className='credential-reveal-section'>
                            {revealedCreds[intervention.commandId].map(
                              (cred) => (
                                <div
                                  key={cred.credentialId}
                                  className='credential-card'
                                >
                                  <div className='credential-meta'>
                                    <span className='credential-label'>
                                      {cred.label}
                                    </span>
                                    {cred.site && (
                                      <span className='credential-site'>
                                        {cred.site}
                                      </span>
                                    )}
                                  </div>
                                  {cred.error && (
                                    <div className='credential-error'>
                                      {cred.error}
                                    </div>
                                  )}
                                  {cred.fields && (
                                    <div className='credential-fields'>
                                      {Object.entries(cred.fields).map(
                                        ([key, value]) => {
                                          const fieldKey = `${cred.credentialId}-${key}`;
                                          const isVisible =
                                            visibleFields.has(fieldKey);
                                          return (
                                            <div
                                              key={key}
                                              className='credential-field-row'
                                            >
                                              <span className='credential-field-label'>
                                                {key}
                                              </span>
                                              <span className='credential-field-value'>
                                                {isVisible
                                                  ? value
                                                  : maskValue(value)}
                                              </span>
                                              <button
                                                className='credential-toggle-btn'
                                                onClick={() =>
                                                  toggleFieldVisibility(
                                                    fieldKey,
                                                  )
                                                }
                                              >
                                                {isVisible ? 'Hide' : 'Show'}
                                              </button>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active Tasks Section */}
          <div className='tasks-section active-section'>
            <h4 className='section-title'>Active</h4>
            {activeTasks.length === 0 ? (
              <div className='empty-state'>No active tasks</div>
            ) : (
              <div className='tasks-list'>
                {activeTasks.map((task) => {
                  const deadlineInfo = formatDeadline(task.deadline);
                  return (
                    <div
                      key={task.id}
                      className='task-row active-task'
                      onClick={() =>
                        task.threadId && handleOpenThread(task.threadId)
                      }
                    >
                      <div className='task-type-icon'>
                        <TaskTypeIcon taskType={task.taskType} />
                      </div>
                      <div className='task-info'>
                        <span className='task-title'>{task.title}</span>
                        <span className='task-meta'>
                          {task.acceptedByUserName && (
                            <span className='task-worker'>
                              {task.acceptedByUserName}
                            </span>
                          )}
                          {deadlineInfo ? (
                            <span
                              className={`task-deadline ${deadlineInfo.className}`}
                            >
                              {deadlineInfo.text}
                            </span>
                          ) : (
                            <span className='task-status-badge'>
                              {task.status}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Completed/Failed/Cancelled Tasks Section */}
          <CompletedTasksSection />
        </div>
      </div>
    </div>
  );
};

export default HumanTasks;
